/* eslint-disable */
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { showNotification } from "@mantine/notifications";
import { customAxios } from "lib/axios";

export interface LeavesExcelParams {
  startDate?: string;
  endDate?: string;
  department?: string | number;
  status?: string | number;
  employeeName?: string;
  search?: string;
}

type DownloadResult = {
  success: boolean;
  filename?: string;
  blob?: Blob;
  message?: string;
};

const buildQuery = (params: Partial<LeavesExcelParams>) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, val]) => {
    if (val !== undefined && val !== null && val !== "" && val !== "all") {
      qs.append(key, String(val));
    }
  });
  return qs.toString();
};

const parseFilename = (header?: string | null) => {
  if (!header) return null;
  const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(header);
  return match ? match[1].replace(/['"]/g, "") : null;
};

const detectZipSignature = async (blob: Blob) => {
  const head = await blob.slice(0, 4).arrayBuffer();
  const bytes = new Uint8Array(head);
  return (
    bytes.length >= 4 &&
    bytes[0] === 0x50 &&
    bytes[1] === 0x4b &&
    bytes[2] === 0x03 &&
    bytes[3] === 0x04
  );
};

const downloadBlob = (blob: Blob, filename = "report.xlsx") => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

const fetchLeavesExcel = async (params: Partial<LeavesExcelParams>) => {
  const qs = buildQuery(params);
  const url = `/reports/attendance/excelFile${qs ? `?${qs}` : ""}`;

  const res = await customAxios.get(url, {
    responseType: "blob",
    headers: {
      Accept:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    },
  });
  return res;
};

export const useDownloadAttendanceExcel = () =>
  useMutation<DownloadResult, AxiosError, Partial<LeavesExcelParams>>(
    async (params) => {
      const res = await fetchLeavesExcel(params);
      const blob = res.data as Blob;
      const contentType = res.headers["content-type"];
      const contentDisposition = res.headers["content-disposition"];

      const looksLikeExcel = await detectZipSignature(blob);

      if (
        looksLikeExcel ||
        contentType?.includes("spreadsheet") ||
        contentType?.includes("zip") ||
        contentType?.includes("octet-stream")
      ) {
        const filename =
          parseFilename(contentDisposition) ||
          `Attendance_report_${params?.startDate ?? ""}_${
            params?.endDate ?? ""
          }.xlsx`;

        downloadBlob(blob, filename);

        showNotification({
          title: "Download started",
          message: `File "${filename}" is downloading...`,
          color: "teal",
        });

        return { success: true, filename, blob };
      }

      const text = await blob.text();
      showNotification({
        title: "Failed to generate report",
        message:
          text.slice(0, 300) ||
          "Unexpected server response. Please check API or auth token.",
        color: "red",
      });

      throw new Error(text);
    },
    {
      onError: (error: AxiosError) => {
        showNotification({
          title: "Download failed",
          message: error?.message || "Something went wrong while downloading.",
          color: "red",
        });
      },
    }
  );
